import { createTheme, type Theme } from '@mui/material/styles';
import { fonts, palettes, radius, type Mode } from '@infinix/tokens';
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/ibm-plex-mono/600.css';

/** MUI theme built from the same tokens as the CSS variables, so MUI parts and our parts match. */
export function buildTheme(mode: Mode): Theme {
  const p = palettes[mode];
  return createTheme({
    palette: {
      mode,
      primary: { main: p.button, contrastText: '#fff' },
      secondary: { main: p.accentText },
      success: { main: p.success },
      warning: { main: p.warning },
      error: { main: p.danger },
      info: { main: p.info },
      background: { default: p.bg, paper: p.panel },
      text: { primary: p.text, secondary: p.dim, disabled: p.faint },
      divider: p.border,
    },
    shape: { borderRadius: radius.button },
    typography: {
      fontFamily: fonts.sans,
      fontSize: 13,
      h1: { fontSize: 27, fontWeight: 700, letterSpacing: '-0.025em' },
      h2: { fontSize: 15, fontWeight: 600 },
      h3: { fontSize: 13.5, fontWeight: 600 },
      body1: { fontSize: 13.5 },
      body2: { fontSize: 13 },
      caption: { fontSize: 11.5, color: p.caption },
      button: { textTransform: 'none', fontWeight: 600, fontSize: 13 },
    },
    components: {
      MuiButtonBase: { defaultProps: { disableRipple: true } },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: radius.button, height: 36, paddingInline: 14, boxShadow: 'none' },
          sizeSmall: { height: 30, paddingInline: 10, fontSize: 12.5 },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { background: p.popover, border: `1px solid ${p.border2}`, borderRadius: radius.menu, backgroundImage: 'none' },
        },
      },
      MuiPopover: { styleOverrides: { paper: { backgroundImage: 'none' } } },
      MuiDialog: {
        styleOverrides: {
          paper: { background: p.panel, border: `1px solid ${p.border2}`, borderRadius: radius.dialog, backgroundImage: 'none' },
        },
      },
      MuiDrawer: { styleOverrides: { paper: { background: p.panel, backgroundImage: 'none' } } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { background: mode === 'dark' ? '#1A1F2E' : '#141826', fontSize: 12, borderRadius: 8, padding: '6px 9px' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { background: p.sunk, borderRadius: radius.input, fontSize: 13.5 },
          notchedOutline: { borderColor: p.border2 },
        },
      },
      MuiSnackbarContent: { styleOverrides: { root: { background: mode === 'dark' ? '#1A1F2E' : '#141826', color: '#ECEEF5' } } },
    },
  });
}
